<template>
  <div class="roadmap-wrapper">
    <h1 class="roadmap-title">Roadmap</h1>
    <div class="roadmap-container">
      <!-- Info Box Column (Left) -->
      <div class="info-box">
        <transition name="fade">
          <div class="info-text">
            <h3>{{ infoBox.title }}</h3>
            <p>{{ infoBox.text }}</p>
          </div>
        </transition>
        <transition name="slide-up">
          <div class="info-image">
            <img :src="require('@/assets/img/firstNFT-logo.png')" alt="Info Image" />
          </div>
        </transition>
      </div>

      <!-- Timeline Column (Middle) -->
      <div class="timeline">
        <div class="vertical-line"></div>
        <div class="progress-fill" :style="{ height: progressHeight }"></div>
      </div>

      <!-- Phases Column (Right) -->
      <transition-group name="card" tag="div" class="phases">
        <div
            v-for="(phase, index) in phases"
            :key="phase.title"
            class="phase"
            :class="{ active: index === currentPhase }"
        >
          <div class="phase-header">
            <!-- Sample icon; replace with your preferred icon if needed -->
            <span class="phase-icon"></span>
            <h3 class="gradient-test">{{ phase.title }}</h3>
          </div>
          <ul>
            <li v-for="(item, idx) in phase.items" :key="idx">{{ item }}</li>
          </ul>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerticalRoadmap',
  data() {
    return {
      // Info box content
      infoBox: {
        title: 'Welcome to Our Roadmap',
        text: 'This box provides a brief overview of our progress and goals.'
      },
      // Roadmap phases
      phases: [
        {
          title: 'Phase 1: Planning',
          items: [
            'Requirement Gathering',
            'Initial Design',
            'Project Setup'
          ]
        },
        {
          title: 'Phase 2: Development',
          items: [
            'Core Features Implementation',
            'Testing & QA',
            'Feedback Iteration'
          ]
        },
        {
          title: 'Phase 3: Deployment',
          items: [
            'Final Testing',
            'Deployment to Production',
            'Post-Launch Support'
          ]
        }
      ],
      // Current active phase (0-based index)
      currentPhase: 1,
      // Fixed height for each phase block (used for progress calculation)
      phaseHeight: 120
    };
  },
  computed: {
    // Calculate progress as a percentage.
    // When currentPhase = 0 => progress = 0%
    // When currentPhase = phases.length - 1 => progress = 100%
    progressHeight() {
      const totalSteps = this.phases.length - 1;
      if (totalSteps <= 0) return '0%';
      const percentage = (this.currentPhase / totalSteps) * 100;
      return percentage + '%';
    }
  }
};
</script>

<style scoped>
/* Overall Wrapper */
.gradient-test{
  background: linear-gradient(90deg, rgba(242, 106, 255, 1), rgba(125, 111, 255, 1));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.roadmap-wrapper {
  margin-bottom: 100px;
  min-height: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Title */
.roadmap-title {
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
}

/* Main Container */
.roadmap-container {
  width: 100%;
  max-width: 1300px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  border-radius: 20px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  background: #fff;
  padding: 20px;
}

/* Info Box */
.info-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
}

.info-text h3 {
  margin-bottom: 10px;
  font-size: 20px;
}

.info-text p {
  font-size: 14px;
  line-height: 1.5;
}

.info-image img {
  width: 100%;
  height: auto;
  display: block;
}

/* Timeline */
.timeline {
  position: relative;
  width: 50px;
  display: flex;
  justify-content: center;
}

.vertical-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 0;
  bottom: 0;
  width: 12px;
  border-radius: 6px;
  background-color: rgba(51, 51, 51, 0.51);
}

.progress-fill {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 12px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  background: linear-gradient(180deg, rgba(242, 106, 255, 1), rgba(125, 111, 255, 1));
  transition: height 0.3s ease-in-out;
}

/* Phases - Card Based Layout */
.phases {
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  padding: 10px;
}

.phase {
  background: #f9f9f9;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.phase.active {
  border: 2px solid rgba(125, 111, 255, 1);
}

.phase:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.phase-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
}

.phase-icon {
  font-size: 24px;
}

/* Transition Classes */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: transform 0.5s ease, opacity 0.5s ease;
}
.slide-up-enter {
  transform: translateY(20px);
  opacity: 0;
}

.card-enter-active, .card-leave-active {
  transition: all 0.5s ease;
}
.card-enter, .card-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .roadmap-container {
    flex-direction: column;
    align-items: center;
  }
  .info-box, .timeline, .phases {
    width: 100%;
    text-align: center;
  }
  .info-box {
    order: 1;
  }
  .timeline {
    order: 2;
    margin: 20px 0;
  }
  .phases {
    order: 3;
  }
}

@media (max-width: 480px) {
  .roadmap-title {
    font-size: 28px;
  }
  .info-text h3 {
    font-size: 18px;
  }
  .phase {
    padding: 10px;
  }
}
</style>
